import fp from "lodash/fp"

import type { Theme } from "@mui/material"
import type { TFunction } from "i18next"
import type { ChartsTextStyle } from "@mui/x-charts"
import type {
  AlertSettingAccordion,
  AlertSettingConfigThreshold,
} from "types/alerts.types"
import type {
  AggregateType,
  DeviceTelemetryParams,
  PowerlineTelemetryParams,
} from "types/telemetries.types"
import type { PlotView, LinePlotView } from "types/dashboard.types"
import type { LabelAlign, YReferenceLine } from "types/viewProperties.types"
import type { NumberToLocaleFn } from "helpers/utils/localeNumber"

import { alertTypeDefs } from "helpers/config/dashboards/global/alerts"
import { lineView } from "helpers/config/dashboards"
import { iceAccretionDetectionPlotView } from "helpers/config/dashboards/device"
import { getAlertFields } from "helpers/utils/alerts"
import { numberToLocale } from "helpers/utils/localeNumber"
import { convertFieldsValue, getFieldFormat } from "helpers/utils/telemetryFields"
import {
  BATTERY_LEVEL_FIELD,
  ICE_ACCRETION_SEVERITY_FIELD,
} from "helpers/config/telemetryFieldDefs"

export const getAlertViewAndFields = (
  alert: AlertSettingAccordion,
  theme: Theme,
  t: TFunction,
  locale = "en",
): { fields: string[]; view: PlotView } => {
  const type = alert.type
  const isThreshold = type === "threshold"
  const isBattery = type === "battery_level"
  const nonThresholdPlotDef = alertTypeDefs[type as keyof typeof alertTypeDefs]
  // Resolved once and threaded down: everything that reads a unit off a field — the axis
  // format, the threshold conversion, the reference lines — has to agree on the same list as
  // the one the data is converted with.
  const fields = getAlertFields(alert)
  const view = isThreshold
    ? getAlertView(alert, fields, theme, t, locale)
    : isBattery
      ? fp.merge(getAlertView(alert, fields, theme, t, locale), nonThresholdPlotDef.view)
      : { ...nonThresholdPlotDef.view }

  return { fields, view }
}

export const getAlertView = (
  alert: AlertSettingAccordion,
  fields: string[],
  theme: Theme,
  t: TFunction,
  locale = "en",
): LinePlotView => {
  const alertThresholds = alert.config.thresholds
  const thresholds = alertThresholds?.length ? alertThresholds : []
  const { min, max } = findMinAndMaxAlertThresholds(thresholds)
  // The bounds share the axis with the plotted data, so they follow the same conversion the
  // reference lines do.
  const bounds = getAlertBounds(
    fields,
    min !== null ? convertFieldsValue(fields, min) : null,
    max !== null ? convertFieldsValue(fields, max) : null,
  )
  const yReferenceLines = getYReferenceLinesFromAlert(alert, fields, theme, t, locale)

  const isIcing = fields.includes(ICE_ACCRETION_SEVERITY_FIELD)

  if (isIcing) {
    return fp.merge(iceAccretionDetectionPlotView, {
      properties: {
        legendColumns: ["_time", "_value", "name"],
        yReferenceLines,
      },
    })
  }

  return fp.merge(lineView, {
    properties: {
      axes: {
        y: {
          ...bounds,
          // The same resolver the entity's own plot uses, so both read alike
          ...getFieldFormat(fields),
        },
      },
      legendColumns: ["_time", "_value", "name"],
      yReferenceLines,
    },
  })
}

export const getYReferenceLinesFromAlert = (
  alert: AlertSettingAccordion,
  fields: string[],
  theme: Theme,
  t: TFunction,
  locale = "en",
): YReferenceLine[] => {
  const thresholds = alert.config.thresholds ?? []
  if (!thresholds?.length) return []
  const formatValue: NumberToLocaleFn = (v) => numberToLocale(v, locale)

  // Thresholds are stored in the field's own unit, while the chart plots the converted
  // value, so the lines have to be converted to sit where the data does. The fields are the
  // caller's, not `alert.config.fields`: those are the ones the plotted data is converted
  // with, and for a non-threshold alert the two lists differ.
  const convert = (value: number) => convertFieldsValue(fields, value)

  return thresholds.flatMap((threshold) => {
    const lines: YReferenceLine[] = []
    const isAlert = threshold.target === "alert"
    const color = isAlert ? theme.palette.error.main : theme.palette.secondary.main

    const labelStyle: ChartsTextStyle = {
      fill: color,
      fontSize: "0.7rem",
      paintOrder: "stroke",
      stroke: theme.palette.background.paper,
      strokeWidth: 3,
    }
    const lineStyle = { strokeDasharray: "4 4", strokeWidth: 0.75, stroke: color }

    const commonStyles = {
      labelAlign: "start" as LabelAlign,
      labelStyle,
      lineStyle,
    }

    const hasMin = threshold.min !== undefined
    const hasMax = threshold.max !== undefined

    if (hasMin && hasMax) {
      const { min, max } = threshold as { min: number; max: number }

      if (min <= max) {
        lines.push(
          {
            y: convert(min),
            label: t(`alert_chart.LABEL.${isAlert ? "ALERT_BETWEEN" : "OK_BETWEEN"}`, {
              min: formatValue(convert(min)),
              max: formatValue(convert(max)),
            }),
            ...commonStyles,
          },
          {
            y: convert(max),
            label: "",
            ...commonStyles,
          },
        )
      } else {
        lines.push(
          {
            y: convert(max),
            label: t(`alert_chart.LABEL.${isAlert ? "ALERT_BELOW" : "OK_BELOW"}`, {
              value: formatValue(convert(max)),
            }),
            ...commonStyles,
          },
          {
            y: convert(min),
            label: t(`alert_chart.LABEL.${isAlert ? "ALERT_ABOVE" : "OK_ABOVE"}`, {
              value: formatValue(convert(min)),
            }),
            ...commonStyles,
          },
        )
      }
    } else if (hasMax) {
      const { max } = threshold as { max: number }
      lines.push({
        y: convert(max),
        label: t(`alert_chart.LABEL.${isAlert ? "ALERT_BELOW" : "OK_BELOW"}`, {
          value: formatValue(convert(max)),
        }),
        ...commonStyles,
      })
    } else if (hasMin) {
      const { min } = threshold as { min: number }
      lines.push({
        y: convert(min),
        label: t(`alert_chart.LABEL.${isAlert ? "ALERT_ABOVE" : "OK_ABOVE"}`, {
          value: formatValue(convert(min)),
        }),
        ...commonStyles,
      })
    }

    return lines
  })
}

export const findMinAndMaxAlertThresholds = (
  thresholds: AlertSettingConfigThreshold[],
): { min: number | null; max: number | null } => {
  // An empty threshold input is stored as null, and a threshold can carry neither bound, so
  // the values are filtered before Math.min/max: a null would be coerced to 0 and pin the
  // axis there, and an undefined would turn the bound into NaN.
  const allThresholds = thresholds
    .flatMap((th) => [th.min, th.max])
    .filter(
      (value): value is number => typeof value === "number" && Number.isFinite(value),
    )
  const min = allThresholds.length > 0 ? Math.min(...allThresholds) : null
  const max = allThresholds.length > 0 ? Math.max(...allThresholds) : null
  return { min, max }
}

export const getAlertBounds = (
  fields: string[],
  min: number | null,
  max: number | null,
): { bounds: [string, string] } => {
  const isBattery = fields.includes(BATTERY_LEVEL_FIELD)
  const minBattery = min !== null && min < 0 ? String(min) : "0"
  const maxBattery = max !== null && max > 100 ? String(max) : "100"

  return isBattery
    ? { bounds: [minBattery, maxBattery] }
    : { bounds: [min !== null ? String(min) : "", max !== null ? String(max) : ""] }
}

export const getAlertAggregate = (alert: AlertSettingAccordion): AggregateType[] =>
  alert.type === "sentrisense_motion" ? ["max"] : ["last"]

export const getQueryParams = (
  alert: AlertSettingAccordion,
  fields: string[],
  dateRange: { from_date: string; to_date: string },
  orgId: number,
  isRawData = false,
): DeviceTelemetryParams | PowerlineTelemetryParams => {
  const isMotion = alert.type === "sentrisense_motion"
  const aggregation = isRawData
    ? {}
    : {
        aggregate: getAlertAggregate(alert),
        aggregate_n: 360,
        aggregate_use_original_time: true,
      }

  switch (alert.entity) {
    case "powerline":
      return {
        ...dateRange,
        field: fields,
        ...aggregation,
        group: ["powerline_id", "_field"],
        measurement: "dlr",
        org_id: orgId,
      }
    case "tower":
      return {
        ...dateRange,
        field: fields,
        ...aggregation,
        group: ["tower_id", "_field"],
        measurement: "tower_health",
        org_id: orgId,
      }
    case "group":
      return {}
    case "device":
    default:
      return {
        ...dateRange,
        field: fields,
        ...aggregation,
        group: ["id", "_field"],
        ...(isMotion && { method: ["toInt"] }),
      }
  }
}
